import { useAtomValue, useSetAtom } from 'jotai';
import { calendarConfigAtom, calendarLoadingAtom, selectedProductAtom } from './calendar-atoms';
import { useCallback, useMemo } from 'react';
import { cn, formatMoney } from '@/entrypoints/utils';

type Props = {
  depositPrice: string;
  storePrice: string;
  reserveSlot: () => Promise<void>;
  onClose: () => void;
};

function CalendarConfirm({ depositPrice, storePrice, reserveSlot, onClose }: Props) {
  const {
    labels,
    klaviyoFormId,
    klaviyoFormIdMobile,
    products,
    payInStoreLabelPrice,
    payDepositLabelPrice,
  } = useAtomValue(calendarConfigAtom);
  const selectedProduct = useAtomValue(selectedProductAtom);
  const setLoading = useSetAtom(calendarLoadingAtom);

  const payInStoreEnabled = useMemo(
    () => products.find((p) => p.id.toString() === selectedProduct)?.pay_in_store,
    [products, selectedProduct],
  );

  const selectedProductData = useMemo(
    () => products.find((p) => p.id.toString() === selectedProduct),
    [products, selectedProduct],
  );

  // Product configs rendered before `checkout_enabled` existed omit the key entirely — only
  // an explicit false hides the deposit checkout option.
  const depositCheckoutEnabled = selectedProductData?.checkout_enabled !== false;

  const handlePayInStore = useCallback(() => {
    if (!klaviyoFormId) return;

    let idToOpen = klaviyoFormId;
    if (window.innerWidth < 768 && klaviyoFormIdMobile) {
      idToOpen = klaviyoFormIdMobile;
    }

    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', idToOpen]);
    onClose();
  }, [klaviyoFormId, klaviyoFormIdMobile, onClose]);

  const handlePayDepositNow = useCallback(() => {
    setLoading(true);
    reserveSlot().finally(() => setLoading(false));
  }, [reserveSlot, setLoading]);

  const payDepositDescription = useMemo(() => {
    return labels.pay_deposit_now_description
      .replace(
        /{deposit_amount}/g,
        formatMoney(selectedProductData?.price ?? 0, '${{amount_no_decimals}}'),
      )
      .replace(
        /{remaining_amount}/g,
        formatMoney(
          (selectedProductData?.compare_at_price ?? 0) - (selectedProductData?.price ?? 0),
          '${{amount_no_decimals}}',
        ),
      );
  }, [labels.pay_deposit_now_description, depositPrice, selectedProductData]);

  // The CTA labels are rendered once server-side but the captions below them are computed
  // per selected product, so a page whose clinics carry different `pay_in_store_amount` or
  // `compare_at_price` values would show a label that contradicts its own caption. Both
  // labels are therefore substituted here, from the same fields the captions use.
  const payInStoreLabel = useMemo(() => {
    // Product metafield first, then the per-block setting the product's dropdown was
    // rendered with, then the page-level fallback from medispa-calendar-config.
    const amount =
      selectedProductData?.pay_in_store_amount ||
      selectedProductData?.pay_in_store_label_price ||
      payInStoreLabelPrice ||
      0;
    return labels.pay_in_store.replace(
      /{pay_in_store_amount}/g,
      formatMoney(amount, '${{amount_no_decimals}}'),
    );
  }, [labels.pay_in_store, selectedProductData, payInStoreLabelPrice]);

  const payDepositLabel = useMemo(() => {
    const amount = selectedProductData?.compare_at_price || payDepositLabelPrice || 0;
    return labels.pay_deposit_now.replace(
      /{total_amount}/g,
      formatMoney(amount, '${{amount_no_decimals}}'),
    );
  }, [labels.pay_deposit_now, selectedProductData, payDepositLabelPrice]);

  const payInStoreDescription = useMemo(() => {
    return labels.pay_in_store_description
      .replace(/{deposit_amount}/g, formatMoney(0, '${{amount_no_decimals}}'))
      .replace(
        /{remaining_amount}/g,
        formatMoney(selectedProductData?.pay_in_store_amount!, '${{amount_no_decimals}}'),
      );
  }, [labels.pay_in_store_description, selectedProductData]);

  return (
    <div className="medispa-calendar_confirm">
      {payInStoreEnabled && (
        <div className="medispa-calendar_confirm-actions">
          <button
            type="button"
            className={cn('ba-btn', !depositCheckoutEnabled ? 'dark solid' : 'outline')}
            onClick={handlePayInStore}
          >
            {payInStoreLabel}
          </button>

          <div className="medispa-calendar-action--details-wrapper">
            <div className="medispa-calendar-action--details">
              {/* <strong className="price">{storePrice}</strong> */}
              <span className="description">{payInStoreDescription}</span>
            </div>
          </div>
        </div>
      )}

      {payInStoreEnabled && depositCheckoutEnabled && (
        <span className="medispa-calendar_confirm-or">{labels.or_choose_to}</span>
      )}

      {depositCheckoutEnabled && (
        <div className="medispa-calendar_confirm-actions accented">
          <button
            type="button"
            onClick={handlePayDepositNow}
            className={cn('ba-btn', (payInStoreEnabled) ? 'dark solid' : 'outline')}
          >
            {payDepositLabel}
          </button>

          <div className="medispa-calendar-action--details-wrapper">
            <div className="medispa-calendar-action--details">
              {/* <strong className="price">{depositPrice}</strong> */}
              <span className="description">{payDepositDescription}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarConfirm;
